import type {
  Clinic,
  ClinicBranding,
  PresignedClinicLogo,
  UpdateClinic,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The clinic's own details. **Clinic-scoped and admin only** — the clinic comes
 * from the access token, so there is no shape in which one clinic reads another.
 */
export function readClinic(signal?: AbortSignal): Promise<Clinic> {
  return api<Clinic>("/clinic", { signal });
}

/**
 * Change the clinic's details. **Admin only**, refused by the server otherwise.
 *
 * The currency is refused once any priced row exists (L2) and comes back as a
 * 409 whose message names the count — it converts nothing, it changes what every
 * stored minor-unit amount means.
 */
export function updateClinic(body: UpdateClinic): Promise<Clinic> {
  return api<Clinic>("/clinic", { method: "PATCH", body });
}

/**
 * The name and logo the sidebar draws, for **every** role.
 *
 * Fetched rather than read from the token, because the URL is signed and
 * short-lived: baking it into the stored `AuthUser` would put an expired
 * address into every reloaded tab.
 */
export function readClinicBranding(signal?: AbortSignal): Promise<ClinicBranding> {
  return api<ClinicBranding>("/clinic/branding", { signal });
}

/** Step one of three: presign, PUT, confirm — the attachment flow (STORY-025). */
export function presignClinicLogo(
  mimeType: string,
  sizeBytes: number,
): Promise<PresignedClinicLogo> {
  return api<PresignedClinicLogo>("/clinic/logo/presign", {
    method: "POST",
    body: { mimeType, sizeBytes },
  });
}

/** Step three. The key is checked by the server, never trusted. */
export function confirmClinicLogo(s3Key: string): Promise<Clinic> {
  return api<Clinic>("/clinic/logo", { method: "POST", body: { s3Key } });
}

/** Remove it. The clinic falls back to its name, as it read before. */
export function removeClinicLogo(): Promise<Clinic> {
  return api<Clinic>("/clinic/logo", { method: "DELETE" });
}
